// filepath: apps/web/src/features/cabinet/useNavManifest.ts
//
// FR-UI-CABINET-1 AC-2 — the SINGLE place the sidebar decides what
// to render.
//
// The sidebar calls `useNavManifest()` and renders exactly the items
// the returned `NavManifest` entitles — nothing client-guessed. This
// is the INV-2 boundary applied to navigation: nav visibility is
// cosmetic UX derived from a SERVER-ISSUED signal, and every linked
// surface remains server-gated by RBAC middleware regardless of what
// the manifest says. The SPA never authorizes an action; it only
// renders nav links.
//
// v1 DERIVATION (no fetch — see `types.ts` for the `/api/me` seam):
//   - alwaysPresent        — constant ["cabinet","tasks"].
//   - processesAccess      — `true` (all-environments authenticated
//                            surface; see NavManifest doc).
//   - adminAccess          — JWT `realm_access.roles` includes the
//                            `admin` role. FAIL-CLOSED: absent /
//                            empty / malformed roles ⇒ `false` ⇒ the
//                            Admin section is NOT rendered.
//   - readableEntityTypes  — `[]` (AC-5 deferred to `/api/me`).
//
// The roles signal is the cryptographically-signed `realm_access`
// claim attested by Keycloak and already validated by the backend's
// issuer+audience check (INV-6). It is NOT client-guessed — the SPA
// reads an already-validated token's claims.
//
// INV-1 (cache-key tenant-scoping): the manifest is memoized on
// `[auth.user, tenantSlug]`. `tenantSlug` is part of the memo
// identity even though the v1 derivation reads only the JWT, because
// (a) the JWT is realm-scoped so `auth.user` already changes on a
// tenant/realm switch, and (b) including `tenantSlug` makes the
// identity match the `['api-me', tenantSlug, subject]` key shape the
// `/api/me` follow-up will adopt (FR-UI-CORE-2 AC-7), so the swap is
// mechanical. A user switching realms gets a fresh manifest — no
// cross-tenant nav leak.

import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { useTenant } from "@/tenant/TenantProvider";
import type { NavManifest } from "./types";

/**
 * The role name the codebase treats as the admin-surface convention.
 * Mirrors `internal/entity/permissions.go` `isAdminBypass` and the
 * `"admin"` seeds used across the test suite. Kept as a single
 * constant so a future rename is a one-place change.
 */
const ADMIN_ROLE = "admin";

/**
 * The role name that gates the directory surface (FR-UI-DIR-1).
 * Kept as a single constant so a future rename is a one-place
 * change. Follows the same JWT-derivation pattern as ADMIN_ROLE;
 * the follow-up `/api/me` FR will replace this with
 * `resolved_permissions.directory_access` (seam documented in
 * `types.ts`).
 */
const DIRECTORY_ROLE = "directory.access";

/**
 * Coerce the raw `realm_access.roles` claim (typed as `unknown`
 * because oidc-client-ts's `User.profile` is a loose record) into a
 * validated `string[]`. Defensive: a malformed claim (non-array,
 * non-string members) yields `[]` — which fail-closes `adminAccess`.
 */
function extractRoles(profile: unknown): string[] {
  if (typeof profile !== "object" || profile === null) return [];
  const realmAccess = (profile as { realm_access?: unknown }).realm_access;
  if (typeof realmAccess !== "object" || realmAccess === null) return [];
  const roles = (realmAccess as { roles?: unknown }).roles;
  if (!Array.isArray(roles)) return [];
  return roles.filter((r): r is string => typeof r === "string");
}

/**
 * Derive the navigation manifest from the server-issued JWT.
 *
 * Fail-closed by construction: any path that fails to produce a
 * validated `["admin"]` membership yields `adminAccess === false`,
 * so the Admin section is omitted entirely (not merely hidden).
 */
export function useNavManifest(): NavManifest {
  const auth = useAuth();
  const { tenantSlug } = useTenant();

  return useMemo<NavManifest>(() => {
    // `tenantSlug` is part of the memo identity for INV-1 cache-key
    // tenant-scoping — see the module header. Referenced here so the
    // exhaustive-deps lint and the SecurityReviewer can see the
    // dependency is load-bearing, not accidental.
    void tenantSlug;

    const roles = extractRoles(auth.user?.profile);
    const adminAccess = roles.includes(ADMIN_ROLE);
    // FR-UI-DIR-1: directory surface access follows the same
    // fail-closed JWT derivation pattern as adminAccess. When the
    // follow-up `/api/me` FR lands, this moves to
    // `resolved_permissions.directory_access`.
    const directoryAccess = roles.includes(DIRECTORY_ROLE);

    return {
      alwaysPresent: ["cabinet", "tasks"] as const,
      processesAccess: true,
      adminAccess,
      directoryAccess,
      readableEntityTypes: [],
    };
  }, [auth.user, tenantSlug]);
}
